import { Maybe } from '@repo/utils'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { Input } from '../../../../shadcn/components/ui/input'
import { cn } from '../../../../shadcn/utils'

type GeolocalisationFieldOptionValue = {
  description: string
  placeId: string
  distance_meters?: number
}

type GeolocalisationFieldProps<T extends FieldValues> = {
  form: UseFormReturn<T>
  name: Path<T>
  placeholder?: string
  disabled?: boolean
  getPlacePredictionsProps?: Partial<google.maps.places.AutocompletionRequest>
}

const GeolocalisationField = <T extends FieldValues>({
  form,
  placeholder,
  name,
  disabled,
  getPlacePredictionsProps,
}: GeolocalisationFieldProps<T>) => {
  const intl = useIntl()
  const [inputValue, setInputValue] = useState<string>(
    form.getValues(name)?.description || '',
  )
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false)
  const [hasSelected, setHasSelected] = useState<boolean>(Boolean(inputValue))
  const containerRef = useRef<HTMLDivElement>(null)

  const { placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_KEY,
    libraries: ['places'],
    sessionToken: false,
  })

  const options: GeolocalisationFieldOptionValue[] = useMemo(
    () =>
      placePredictions.map(({ description, place_id, distance_meters }) => ({
        description,
        placeId: place_id,
        distance_meters,
      })),
    [placePredictions],
  )

  const onInputChange = (value: string): void => {
    setInputValue(value)
    setHasSelected(false)
    if (value) {
      setShowSuggestions(true)
      getPlacePredictions({
        input: value,
        ...getPlacePredictionsProps,
      })
    } else {
      setShowSuggestions(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        if (!hasSelected) {
          setInputValue('')
        }
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [hasSelected])

  const onSelect = (
    value: Maybe<GeolocalisationFieldOptionValue>,
  ): Maybe<GeolocalisationFieldOptionValue> => {
    setInputValue(value?.description || '')
    setHasSelected(true)
    setShowSuggestions(false)

    if (!value) {
      return null
    }

    return value
  }

  return (
    <div ref={containerRef} className='w-full'>
      <Controller
        name={name}
        control={form.control}
        render={({ field }) => {
          return (
            <div className='relative'>
              <Input
                placeholder={
                  placeholder ??
                  intl.formatMessage({
                    id: 'ui.GeolocalisationField.placeholder',
                    defaultMessage: 'Entrez une adresse',
                  })
                }
                value={inputValue}
                onChange={(e) => {
                  onInputChange(e.target.value)
                  field.onChange(undefined)
                }}
                disabled={disabled}
                onFocus={() => inputValue && setShowSuggestions(true)}
                autoComplete='off'
              />

              {showSuggestions && options.length > 0 && (
                <div
                  className={cn(
                    'border-gray-300 r-0 l-0 absolute z-10 mt-2 max-h-60 overflow-auto rounded border border-grey-500 bg-white shadow-lg',
                  )}
                >
                  {options.map((option) => (
                    <button
                      key={option.placeId}
                      type='button'
                      className='w-full cursor-pointer px-3 py-2 text-left hover:bg-grey-300'
                      onMouseDown={(e) => {
                        e.preventDefault()
                        const selectedAddress = onSelect(option)
                        field.onChange(selectedAddress)
                      }}
                    >
                      {option.description}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        }}
      />
    </div>
  )
}

export default GeolocalisationField
